import React, { useRef, useState } from "react";
import { Box, Button, Stack, Typography, TextField } from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import * as htmlToImage from "html-to-image";
import selicoLogo from "../images/Logo/selico.logo.svg";

const prefixDescriptions: Record<string, string> = {
  "11": "Bak Klein (Blauw)",
  "12": "Bak Klein (Geel)",
  "13": "Bak Klein (Groen)",
  "14": "Bak Klein (Rood)",
  "21": "Bak Groot Gesloten (Grijs)",
  "22": "Bak Groot Open (Zwart)",
  "31": "Schap",
  "41": "Makita bak",
};

export default function SelicoLabelGenerator() {
  const [code, setCode] = useState<string>("11-0002");

  const labelRef = useRef<HTMLDivElement>(null);

  const exportRef = useRef<HTMLDivElement>(null);

  const prefix = code.slice(0, 2);
  const description = prefixDescriptions[prefix] || "Onbekend type";

  const downloadPng = async () => {
    if (!exportRef.current) return;
    const dataUrl = await htmlToImage.toPng(exportRef.current, {
      pixelRatio: 2,
      backgroundColor: "#FFFFFF",
    });

    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `${code}.png`;
    a.click();
  };

  const printLabel = () => {
    if (!exportRef.current) return;
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <style>
            body {
              margin: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              font-family: 'Google Sans', sans-serif;
              background: white;
            }
          </style>
        </head>
        <body>${exportRef.current.outerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  return (
    <Stack spacing={3} alignItems="center" sx={{ fontFamily: "'Google Sans', sans-serif" }}>
      <TextField
        label="Code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        helperText="Example: 11-0002"
        sx={{ width: 300 }}
      />

      <Box
        ref={exportRef}
        sx={{
          padding: "20px",
          backgroundColor: "#FFFFFF",
          display: "inline-block",
        }}
      >
        <Box
          ref={labelRef}
          sx={{
            width: "1000px",
            border: "10px solid white",
            borderRadius: "20px",
            backgroundColor: "#FFFFFF",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: "6px",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              width: "100%",
              border: "3px solid #399D9B",
              borderRadius: "inherit",
              backgroundColor: "#FFFFFF",
              p: "6px",
              boxSizing: "border-box",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                border: "3px solid white",
                borderRadius: "inherit",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "25px 30px",
                boxSizing: "border-box",
                backgroundColor: "#FFFFFF",
              }}
            >
              <QRCodeCanvas value={code} size={180} bgColor="#FFFFFF" includeMargin={false} />

              <Box
                sx={{
                  flex: 1,
                  textAlign: "center",
                  px: 4,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "56px",
                    fontWeight: "bold",
                    color: "#1C1C1C",
                    lineHeight: 1.1,
                  }}
                >
                  {code}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "28px",
                    fontWeight: "bold",
                    color: "#404040",
                    lineHeight: 1.2,
                  }}
                >
                  {description}
                </Typography>
              </Box>

              <Box
                component="img"
                src={selicoLogo}
                alt="Selico logo"
                sx={{
                  height: "110px",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={downloadPng}>
          Download PNG
        </Button>
        <Button variant="outlined" onClick={printLabel}>
          Print
        </Button>
      </Stack>
    </Stack>
  );
}
